/**
 * Production-safe console logger
 * Automatically disables console output in production
 */

const isDevelopment = process.env.NODE_ENV !== 'production';

/**
 * Production-safe logger object
 * Usage: import { log } from './utils/productionLogger';
 * Then use log.debug(), log.info(), log.warn(), log.error()
 */
export const log = {
  /**
   * Debug-level logging (only in development)
   * @param {...any} args - Arguments to log
   */
  debug: (...args) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },

  /**
   * Info-level logging (only in development)
   * @param {...any} args - Arguments to log
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info(...args);
    }
  },

  /**
   * Warning-level logging (always shown)
   * @param {...any} args - Arguments to log
   */
  warn: (...args) => {
    console.warn(...args);
  },

  /**
   * Error-level logging (always shown)
   * @param {...any} args - Arguments to log
   */
  error: (...args) => {
    console.error(...args);
  },

  /**
   * Table logging (only in development)
   * @param {any} data - Data to display in table format
   */
  table: (data) => {
    if (isDevelopment && console.table) {
      console.table(data);
    }
  },

  /**
   * Group logging (only in development)
   * @param {string} label - Group label
   */
  group: (label) => {
    if (isDevelopment && console.group) {
      console.group(label);
    }
  },

  /**
   * End group logging (only in development)
   */
  groupEnd: () => {
    if (isDevelopment && console.groupEnd) {
      console.groupEnd();
    }
  },

  /**
   * Time measurement start (only in development)
   * @param {string} label - Timer label
   */
  time: (label) => {
    if (isDevelopment && console.time) {
      console.time(label);
    }
  },

  /**
   * Time measurement end (only in development)
   * @param {string} label - Timer label
   */
  timeEnd: (label) => {
    if (isDevelopment && console.timeEnd) {
      console.timeEnd(label);
    }
  },

  /**
   * Conditional logging
   * @param {boolean} condition - Condition to check
   * @param {...any} args - Arguments to log if condition is true
   */
  assert: (condition, ...args) => {
    if (isDevelopment && !condition) {
      console.error('Assertion failed:', ...args);
    }
  }
};

/**
 * Disable all console methods in production
 * Call this once at app startup to completely disable console
 */
export const disableConsoleInProduction = () => {
  if (!isDevelopment) {
    const noop = () => {};
    const methods = ['log', 'debug', 'info', 'warn', 'error', 'table', 'group', 'groupEnd', 'time', 'timeEnd'];
    
    // Only disable log, debug, and info in production
    // Keep warn and error for critical issues
    ['log', 'debug', 'info', 'table', 'group', 'groupEnd', 'time', 'timeEnd'].forEach(method => {
      if (console[method]) {
        console[method] = noop;
      }
    });
  }
};

// Auto-disable console in production (optional - comment out if not desired)
// disableConsoleInProduction();

export default log;
